// src/components/Dialogs/AddBalanceDialog.js - FIXED: Enhanced with Dual Auth Support (Creator + User)

import React, { useState, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  Typography,
  Button,
  TextField,
  Grid,
  Paper,
  IconButton,
  CircularProgress,
  Collapse,
  Divider,
  Alert,
} from '@mui/material';
import {
  Close as CloseIcon,
  CreditCardOutlined,
  Payment as PaymentIcon,
} from '@mui/icons-material';
import { useMutation, useQueryClient } from "react-query";
import { toast } from 'sonner';
import client from "../../API";
import useAuth from '../../Hooks/use-auth';
import { useCreatorAuth } from '../../Hooks/creator-use-auth';
import { useTheme, alpha } from '@mui/material/styles';

const STRIPE_FEE_PERCENT = 0.035;
const STRIPE_FEE_FIXED = 0.30;

const AddBalanceDialog = ({ open, onClose }) => {
  const theme = useTheme();
  const [selectedMethod, setSelectedMethod] = useState('');
  const [amount, setAmount] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const queryClient = useQueryClient();
  
  // Dual Auth Support - similar to TrophyRoom and BalanceLedgerModal
  const { getCurrrentUser, isAuthorized: isUserAuthorized } = useAuth();
  const { creatorToken, isAuthorized: isCreatorAuthorized } = useCreatorAuth();

  // Determine current user based on auth type
  const userInfo = useMemo(() => {
    if (isCreatorAuthorized && creatorToken?.creator_user) {
      return creatorToken.creator_user;
    }
    if (isUserAuthorized) {
      return getCurrrentUser && getCurrrentUser();
    }
    return null;
  }, [isCreatorAuthorized, isUserAuthorized, creatorToken, getCurrrentUser]);

  const breakdown = useMemo(() => {
    const credit = parseFloat(amount) || 0;
    if (credit <= 0 || selectedMethod !== 'stripe') return null;
    const fee = Math.round((credit * STRIPE_FEE_PERCENT + STRIPE_FEE_FIXED) * 100) / 100;
    return { credit, fee, total: credit + fee };
  }, [amount, selectedMethod]);

  // PayPal Order Creation Mutation
  const createPayPalOrderMutation = useMutation(
    (paymentData) => {
      toast.info('Creating PayPal order...', {
        description: 'Redirecting to PayPal for payment'
      });
      return client.balanceLedger.createPayPalOrder(paymentData);
    },
    {
      onSuccess: (data) => {
        if (data.approval_url) {
          toast.success('PayPal order created successfully', {
            description: 'Redirecting to PayPal...'
          });
          // Redirect to PayPal for payment approval
          window.location.href = data.approval_url;
        } else {
          toast.error('PayPal order creation failed', {
            description: 'No approval URL received'
          });
          setError('PayPal approval URL not received');
          setLoading(false);
        }
      },
      onError: (error) => {
        const errorMessage = error.response?.data?.error || 
                           error.response?.data?.message || 
                           error.message || 
                           'Failed to create PayPal order. Please try again.';
        toast.error('PayPal order creation failed', {
          description: errorMessage
        });
        setError(errorMessage);
        setLoading(false);
      }
    }
  );

  // Stripe Checkout Session Creation Mutation
  const createStripeCheckoutSessionMutation = useMutation(
    (paymentData) => {
      toast.info('Creating Stripe checkout session...', {
        description: 'Redirecting to Stripe for payment'
      });
      return client.balanceLedger.createStripeCheckoutSession(paymentData);
    },
    {
      onSuccess: (data) => {
        if (data.url) {
          toast.success('Stripe checkout session created', {
            description: 'Redirecting to Stripe...'
          });
          // Redirect to Stripe for payment
          window.location.href = data.url;
        } else {
          toast.error('Stripe checkout creation failed', {
            description: 'No checkout URL received'
          });
          setError('Stripe checkout URL not received');
          setLoading(false);
        }
      },
      onError: (error) => {
        const errorMessage = error.response?.data?.error || 
                           error.response?.data?.message || 
                           error.message || 
                           'Failed to create Stripe checkout session. Please try again.';
        toast.error('Stripe checkout creation failed', {
          description: errorMessage
        });
        setError(errorMessage);
        setLoading(false);
      }
    }
  );


  const paymentMethods = [
    {
      id: 'paypal',
      name: 'PayPal',
      description: 'Pay with your PayPal account',
      icon: <PaymentIcon sx={{ fontSize: 28 }} />,
      color: '#0070ba',
      processingTime: 'Instant',
      fee: '2.9% + $0.30',
      disabled: false
    },
    {
      id: 'stripe',
      name: 'Card / Bank Transfer',
      description: 'Pay by card, Apple Pay, Google Pay, or bank transfer',
      icon: <CreditCardOutlined sx={{ fontSize: 28, color: '#635bff' }} />,
      color: '#635bff',
      processingTime: 'Instant',
      fee: '3.5% + $0.30',
      disabled: false
    },
  ];

  const resetDialog = () => {
    setSelectedMethod('');
    setAmount('');
    setError('');
    setLoading(false);
  };

  const handleClose = () => {
    resetDialog();
    onClose();
  };

  const handleMethodSelect = (methodId) => {
    setSelectedMethod(prev => prev === methodId ? '' : methodId);
    setError('');
  };

  const handlePaymentSubmit = async () => {
    if (!amount || parseFloat(amount) <= 0) {
      setError('Please enter a valid amount');
      return;
    }

    setLoading(true);
    setError('');

    try {
      if (selectedMethod === 'paypal') {
        await createPayPalOrderMutation.mutateAsync({
          amount: parseFloat(amount),
        });
      } else if (selectedMethod === 'stripe') {
        // Create Stripe checkout session and redirect
        if (!userInfo?.id && !userInfo?.username) {
          setError('User information not available. Please refresh the page and try again.');
          setLoading(false);
          return;
        }

        await createStripeCheckoutSessionMutation.mutateAsync({
          amount: parseFloat(amount),
          user_id: userInfo.id || userInfo.username,
        });
      } else {
        setError('Invalid payment method selected');
        setLoading(false);
      }
    } catch (error) {
      console.error('Payment submission error:', error);
      setError('Payment submission failed. Please try again.');
      setLoading(false);
    }
  };

  const selectedMethodData = paymentMethods.find(m => m.id === selectedMethod);

  return (
    <Dialog
      open={open}
      onClose={!loading ? handleClose : undefined}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          overflow: 'hidden',
        }
      }}
      sx={{ zIndex: theme.zIndex.modal + 2 }}
    >
      {/* Header */}
      <DialogTitle sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        px: 3,
        py: 2,
        borderBottom: `1px solid ${theme.palette.divider}`,
      }}>
        <Typography variant="h6" fontWeight={600}>
          Add Funds
        </Typography>
        <IconButton onClick={handleClose} size="small" disabled={loading}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ px: 3, pt: 3, pb: 3 }}>
        {/* Loading overlay */}
        {loading && (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, py: 5 }}>
            <CircularProgress size={48} />
            <Typography variant="body1" color="text.secondary">
              {selectedMethod === 'paypal' ? 'Redirecting to PayPal…' : 'Redirecting to checkout…'}
            </Typography>
          </Box>
        )}

        {!loading && (
          <>
            {error && (
              <Alert severity="error" sx={{ mb: 2.5 }} onClose={() => setError('')}>
                {error}
              </Alert>
            )}

            {/* Payment method cards */}
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Choose a payment method
            </Typography>

            <Grid container spacing={1.5}>
              {paymentMethods.map((method) => {
                const isSelected = selectedMethod === method.id;
                return (
                  <Grid item xs={12} key={method.id}>
                    <Paper
                      onClick={() => handleMethodSelect(method.id)}
                      elevation={isSelected ? 4 : 1}
                      sx={{
                        p: 2,
                        cursor: 'pointer',
                        border: '2px solid',
                        borderColor: isSelected ? method.color : 'transparent',
                        borderRadius: 2,
                        backgroundColor: isSelected ? alpha(method.color, 0.06) : 'background.paper',
                        transition: 'border-color 0.2s ease, box-shadow 0.2s ease, background-color 0.2s ease',
                        '&:hover': {
                          borderColor: method.color,
                          boxShadow: `0 2px 16px ${alpha(method.color, 0.18)}`,
                        },
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Box sx={{
                          color: method.color,
                          width: 44,
                          height: 44,
                          borderRadius: '50%',
                          flexShrink: 0,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          backgroundColor: alpha(method.color, 0.1),
                        }}>
                          {method.icon}
                        </Box>
                        <Box>
                          <Typography variant="subtitle2" fontWeight={600} lineHeight={1.3}>
                            {method.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" lineHeight={1.3}>
                            {method.description}
                          </Typography>
                        </Box>
                      </Box>
                    </Paper>
                  </Grid>
                );
              })}
            </Grid>

            {/* Amount input — slides in when a method is selected */}
            <Collapse in={!!selectedMethod} unmountOnExit>
              <Box sx={{ mt: 3 }}>
                <Divider sx={{ mb: 3 }} />

                <TextField
                  label="Amount"
                  type="number"
                  fullWidth
                  autoFocus
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <Typography sx={{ mr: 0.5, color: 'text.secondary', fontSize: '1.1rem' }}>$</Typography>
                    ),
                  }}
                  inputProps={{ min: 1, step: 1 }}
                  sx={{ mb: 2 }}
                />

                {/* Quick amounts */}
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 3 }}>
                  {[25, 50, 100, 250, 500].map((preset) => (
                    <Button
                      key={preset}
                      variant={amount === String(preset) ? 'contained' : 'outlined'}
                      size="small"
                      onClick={() => setAmount(String(preset))}
                      sx={{
                        textTransform: 'none',
                        borderRadius: 5,
                        minWidth: 52,
                        ...(amount === String(preset) && selectedMethodData ? {
                          backgroundColor: selectedMethodData.color,
                          borderColor: selectedMethodData.color,
                          '&:hover': { backgroundColor: selectedMethodData.color, filter: 'brightness(0.9)' },
                        } : {}),
                      }}
                    >
                      ${preset}
                    </Button>
                  ))}
                </Box>

                {/* Fee breakdown — shown when a Stripe amount is entered */}
                {breakdown && (
                  <Paper
                    elevation={0}
                    sx={{
                      mb: 2.5,
                      px: 2,
                      py: 1.5,
                      borderRadius: 2,
                      backgroundColor: theme.palette.mode === 'dark'
                        ? alpha(theme.palette.common.white, 0.04)
                        : alpha(theme.palette.common.black, 0.03),
                      border: `1px solid ${theme.palette.divider}`,
                    }}
                  >
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.75 }}>
                      <Typography variant="body2" color="text.secondary">Amount</Typography>
                      <Typography variant="body2">${breakdown.credit.toFixed(2)}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="body2" color="text.secondary">Processing fee</Typography>
                      <Typography variant="body2">${breakdown.fee.toFixed(2)}</Typography>
                    </Box>
                    <Divider sx={{ mb: 1 }} />
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2" fontWeight={600}>Total due</Typography>
                      <Typography variant="body2" fontWeight={600}>${breakdown.total.toFixed(2)}</Typography>
                    </Box>
                  </Paper>
                )}

                {/* Continue button */}
                <Button
                  variant="contained"
                  fullWidth
                  size="large"
                  onClick={handlePaymentSubmit}
                  disabled={!amount || parseFloat(amount) <= 0}
                  sx={{
                    py: 1.4,
                    fontWeight: 600,
                    fontSize: '1rem',
                    textTransform: 'none',
                    borderRadius: 2,
                    ...(selectedMethodData ? {
                      backgroundColor: selectedMethodData.color,
                      '&:hover': { backgroundColor: selectedMethodData.color, filter: 'brightness(0.9)' },
                      '&:disabled': { backgroundColor: alpha(selectedMethodData.color, 0.4) },
                    } : {}),
                  }}
                >
                  {breakdown
                    ? `Continue — Pay $${breakdown.total.toFixed(2)}`
                    : amount && parseFloat(amount) > 0
                      ? `Continue — $${parseFloat(amount).toFixed(2)}`
                      : 'Enter an amount'}
                </Button>
              </Box>
            </Collapse>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default AddBalanceDialog;